/**
 * Utilitaires pour parser les réponses utilisateur du questionnaire
 * 
 * PROBLÈME RÉSOLU :
 * Le code pour extraire le nombre d'habitants était dupliqué 3 fois avec des bugs potentiels.
 * Cette centralisation garantit un comportement cohérent partout.
 * 
 * USAGE :
 * const nbHabitants = parseNombreHabitants(answers.nombre_habitants);
 */

/**
 * Parse le nombre d'habitants depuis la réponse utilisateur
 * 
 * Gère les formats multiples :
 * - Nombre direct : 4
 * - String nombre : '4'
 * - Format option_id : '4_personnes'
 * - Format texte : '5 personnes ou plus'
 * 
 * @param answer Réponse brute de l'utilisateur
 * @returns Nombre d'habitants (minimum 1)
 * 
 * @example
 * parseNombreHabitants(4) // → 4
 * parseNombreHabitants('3') // → 3
 * parseNombreHabitants('4_personnes') // → 4
 * parseNombreHabitants('5_personnes_ou_plus') // → 5
 * parseNombreHabitants(null) // → 1 (défaut)
 */
export function parseNombreHabitants(answer: any): number {
  // Cas null/undefined : retourner valeur par défaut
  if (answer === null || answer === undefined) {
    return 1;
  }
  
  // Cas nombre direct
  if (typeof answer === 'number') {
    return answer > 0 ? Math.floor(answer) : 1;
  }
  
  // Convertir en string pour traitement
  const str = String(answer).trim();
  
  // Cas string vide
  if (str === '') {
    return 1;
  }
  
  // Essayer parseInt direct (pour format '4')
  const directParse = parseInt(str, 10);
  if (!isNaN(directParse) && directParse > 0) {
    return directParse;
  }
  
  // Extraire le premier nombre du string (pour format '4_personnes', '5 personnes ou plus')
  const match = str.match(/(\d+)/);
  if (match) {
    const extracted = parseInt(match[1], 10);
    return extracted > 0 ? extracted : 1;
  }
  
  // Aucun nombre trouvé : retourner défaut
  console.warn('[parseNombreHabitants] Unable to parse:', answer, '- returning default 1');
  return 1;
}

/**
 * Parse un ID d'option de réponse pour extraire la valeur significative
 * 
 * @param optionId ID de l'option (ex: 'ite_autorisee', 'isolation_recente')
 * @returns La partie significative après le dernier underscore
 * 
 * @example
 * parseOptionValue('ite_autorisee') // → 'autorisee'
 * parseOptionValue('isolation_recente') // → 'recente'
 */
export function parseOptionValue(optionId: string | null | undefined): string {
  if (!optionId) return '';
  
  const parts = optionId.split('_');
  return parts[parts.length - 1];
}

/**
 * Vérifie si une réponse correspond à une valeur attendue
 * Gère les variations (selected_option, selected_option_id, selected_options)
 * 
 * @param answer Réponse de l'utilisateur (objet QuestionAnswer)
 * @param expectedValue Valeur attendue (ex: 'isolation_recente')
 * @returns true si la réponse correspond
 * 
 * @example
 * const answer = { question_id: 'isolation_murs_3', selected_option_id: 'isolation_recente' };
 * answerMatchesValue(answer, 'isolation_recente') // → true
 */
export function answerMatchesValue(
  answer: any,
  expectedValue: string
): boolean {
  if (!answer) return false;
  
  // Vérifier selected_option_id
  if (answer.selected_option_id === expectedValue) {
    return true;
  }
  
  // Vérifier selected_option
  if (answer.selected_option === expectedValue) {
    return true;
  }
  
  // Vérifier selected_options (array)
  if (Array.isArray(answer.selected_options)) {
    return answer.selected_options.includes(expectedValue);
  }
  
  return false;
}

/**
 * Récupère une réponse par question_id depuis un tableau de réponses
 * 
 * @param answers Tableau de réponses
 * @param questionId ID de la question recherchée (peut être partiel)
 * @param partial Si true, fait une recherche partielle (includes)
 * @returns La réponse trouvée ou undefined
 * 
 * @example
 * getAnswerByQuestionId(answers, 'isolation_murs_3') // Recherche exacte
 * getAnswerByQuestionId(answers, 'isolation_murs', true) // Recherche partielle
 */
export function getAnswerByQuestionId(
  answers: any[],
  questionId: string,
  partial: boolean = false
): any | undefined {
  if (!Array.isArray(answers)) {
    return undefined;
  }
  
  if (partial) {
    return answers.find(a => a.question_id?.includes(questionId));
  }
  
  return answers.find(a => a.question_id === questionId);
}

/**
 * Extrait la valeur sélectionnée d'une réponse
 * Gère les différents formats de réponse
 * 
 * @param answer Réponse de l'utilisateur
 * @returns La valeur sélectionnée ou null
 * 
 * @example
 * getSelectedValue({ selected_option_id: 'isolation_recente' }) // → 'isolation_recente'
 * getSelectedValue({ selected_option: 'yes' }) // → 'yes'
 * getSelectedValue({ selected_options: ['opt1', 'opt2'] }) // → ['opt1', 'opt2']
 */
export function getSelectedValue(answer: any): any {
  if (!answer) return null;
  
  // Priorité : selected_option_id > selected_option > selected_options
  if (answer.selected_option_id !== undefined) {
    return answer.selected_option_id;
  }
  
  if (answer.selected_option !== undefined) {
    return answer.selected_option;
  }
  
  if (answer.selected_options !== undefined) {
    return answer.selected_options;
  }
  
  return null;
}

/**
 * Parse une année depuis différents formats
 * 
 * @param value Valeur brute (peut être string, number, ou objet Date)
 * @returns Année en nombre ou null
 * 
 * @example
 * parseYear('2015') // → 2015
 * parseYear(2015) // → 2015
 * parseYear('2015-01-01') // → 2015
 * parseYear(null) // → null
 */
export function parseYear(value: any): number | null {
  if (value === null || value === undefined) {
    return null;
  }
  
  // Si c'est déjà un nombre
  if (typeof value === 'number') {
    return Math.floor(value);
  }
  
  // Si c'est une string
  const str = String(value).trim();
  
  // Extraire l'année (4 chiffres)
  const match = str.match(/(\d{4})/);
  if (match) {
    const year = parseInt(match[1], 10);
    // Validation basique (années entre 1900 et 2100)
    if (year >= 1900 && year <= 2100) {
      return year;
    }
  }
  
  return null;
}

/**
 * Vérifie si un système est récent (moins de N ans)
 * 
 * @param anneeInstallation Année d'installation
 * @param seuilAns Nombre d'années pour être considéré comme récent (défaut: 15)
 * @returns true si le système est récent
 * 
 * @example
 * isSystemRecent('2020', 15) // → true (si on est en 2024)
 * isSystemRecent('2005', 15) // → false
 * isSystemRecent(null, 15) // → false (âge inconnu = pas considéré récent)
 */
export function isSystemRecent(
  anneeInstallation: any,
  seuilAns: number = 15
): boolean {
  const year = parseYear(anneeInstallation);
  
  if (year === null) {
    return false; // Si âge inconnu, on considère comme potentiellement ancien
  }
  
  const currentYear = new Date().getFullYear();
  const age = currentYear - year;
  
  return age < seuilAns;
}

/**
 * Parse un volume de ballon ECS depuis une string
 * 
 * @param description Description du système ECS (ex: "Ballon 150L")
 * @returns Volume en litres ou null
 * 
 * @example
 * parseVolumeECS('Ballon électrique 150L') // → 150
 * parseVolumeECS('Chauffe-eau 200 litres') // → 200
 * parseVolumeECS('Ballon instantané') // → null
 */
export function parseVolumeECS(description: string | null | undefined): number | null {
  if (!description) return null;
  
  const str = description.toLowerCase();
  
  // Rechercher un nombre suivi de 'l' ou 'litre'
  const match = str.match(/(\d+)\s*(?:l(?:itre)?s?)\b/i);
  
  if (match) {
    const volume = parseInt(match[1], 10);
    // Validation : volume entre 10 et 1000 litres
    if (volume >= 10 && volume <= 1000) {
      return volume;
    }
  }
  
  return null;
}

/**
 * Normalise une réponse booléenne depuis différents formats
 * 
 * @param value Valeur brute (peut être boolean, string, number)
 * @returns Boolean ou null si indéterminé
 * 
 * @example
 * parseBoolean(true) // → true
 * parseBoolean('true') // → true
 * parseBoolean('oui') // → true
 * parseBoolean('yes') // → true
 * parseBoolean(1) // → true
 * parseBoolean('non') // → false
 * parseBoolean(null) // → null
 */
export function parseBoolean(value: any): boolean | null {
  if (value === null || value === undefined) {
    return null;
  }
  
  // Si déjà boolean
  if (typeof value === 'boolean') {
    return value;
  }
  
  // Si number
  if (typeof value === 'number') {
    return value !== 0;
  }
  
  // Si string
  const str = String(value).toLowerCase().trim();
  
  // Valeurs true
  if (['true', 'yes', 'oui', '1', 'vrai'].includes(str)) {
    return true;
  }
  
  // Valeurs false
  if (['false', 'no', 'non', '0', 'faux'].includes(str)) {
    return false;
  }
  
  return null;
}